import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

const getAuthHeaders = () => {
  const token = localStorage.getItem("access");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeProducts = (productos) => {
  if (!Array.isArray(productos)) return [];

  return productos.map((r) => ({
    product_id: r.product_id ?? r.product_id_ref ?? null,
    variant_id: r.variant_id ?? r.variant_id_ref ?? null,
    code: r.code || "",
    sku: r.sku || "",
    variant_type: r.variant_type || "",
    value: r.value || r.variant_value || "",
    codigo_principal: r.codigo_principal || "",
    codigo_auxiliar: r.codigo_auxiliar || "",
    descripcion: r.descripcion || "",
    detalles: r.detalles || r.detalle || r.detalle_adicional || "",
    cantidad: String(r.cantidad ?? "1"),
    pvp: String(r.pvp ?? r.precio_unitario ?? "0"),
    iva_porcentaje: Number(r.iva_porcentaje ?? 0),
    descuentoPct: String(r.descuentoPct ?? r.descuento_pct ?? "0"),
  }));
};

const getDocNumber = (doc) => {
  return (
    doc?.numero_documento ||
    doc?.numero ||
    doc?.secuencial ||
    doc?.codigo ||
    `${doc?.establecimiento || ""}-${doc?.punto_emision || ""}-${doc?.secuencial || ""}`
  );
};

export default function DocumentSelectorModal({
  open,
  onClose,
  persona,
  title = "Seleccionar documento",
  tipo = "FACTURA",
  endpoint = "/facturas/documentos-relacionados/facturas-cliente/",
  detailEndpoint = "/facturas/documentos-relacionados/detalle-factura/",
  onSelected,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [loadingDetailId, setLoadingDetailId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  const personaId = persona?.id || persona?.identificacion || "";

  const filteredDocuments = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return documents;

    return documents.filter((doc) => {
      const text = [
        getDocNumber(doc),
        doc?.cliente,
        doc?.razon_social,
        doc?.identificacion,
        doc?.fecha_emision,
        doc?.total,
        doc?.estado,
        doc?.clave_acceso,
      ]
        .join(" ")
        .toLowerCase();

      return text.includes(q);
    });
  }, [documents, query]);

  useEffect(() => {
    if (!open) return;

    const loadDocuments = async () => {
      setLoading(true);
      setError("");
      setDocuments([]);

      try {
        const params = new URLSearchParams();

        if (personaId) params.set("persona_id", personaId);
        if (persona?.identificacion) params.set("identificacion", persona.identificacion);
        if (tipo) params.set("tipo_documento", tipo);

        const url = `${endpoint}?${params.toString()}`;

        const res = await fetch(url, {
          credentials: "include",
          headers: {
            ...getAuthHeaders(),
          },
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          setError(json.error || json.detail || "No se pudieron cargar los documentos.");
          return;
        }

        const lista =
          json.documentos ||
          json.facturas ||
          json.results ||
          json.data ||
          [];

        setDocuments(Array.isArray(lista) ? lista : []);
      } catch {
        setError("No se pudieron cargar los documentos.");
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [open, personaId, persona?.identificacion, tipo, endpoint]);

  const handleSelect = async (doc) => {
    const docId = doc?.id || doc?.documento_id;

    if (!docId) return;

    setLoadingDetailId(docId);
    setError("");

    try {
      const res = await fetch(`${detailEndpoint}${docId}/`, {
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
        },
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(json.error || json.detail || "No se pudo cargar el detalle del documento.");
        return;
      }

      const selectedDoc = json.documento || json.factura || json.data || doc;
      const productos =
        json.productos ||
        json.detalles ||
        selectedDoc.productos ||
        selectedDoc.detalles ||
        [];

      onSelected?.(selectedDoc, normalizeProducts(productos));
      onClose?.();
    } catch {
      setError("No se pudo cargar el detalle del documento.");
    } finally {
      setLoadingDetailId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={isMobile}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800 }}>{title}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por número, cliente, identificación, clave o total"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: "#fff3f3", border: "1px solid #ffcdd2" }}>
            <Typography sx={{ color: "#b71c1c", fontWeight: 700 }}>{error}</Typography>
          </Paper>
        )}

        {loading ? (
          <Box sx={{ py: 5, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : filteredDocuments.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 700, color: "#555" }}>
              No hay documentos disponibles
            </Typography>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {filteredDocuments.map((doc) => {
              const docId = doc?.id || doc?.documento_id;

              return (
                <Paper key={docId} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
                  <Typography sx={{ fontWeight: 800 }}>{getDocNumber(doc)}</Typography>
                  <Typography sx={{ fontSize: 13, color: "#555" }}>
                    {doc?.fecha_emision || ""} {doc?.estado ? `• ${doc.estado}` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#555", mt: 0.5 }}>
                    {doc?.cliente || doc?.razon_social || ""}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#333", fontWeight: 700, mt: 0.5 }}>
                    Total: {doc?.total ?? doc?.total_documento ?? ""}
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ mt: 1.5, bgcolor: "#1565C0" }}
                    onClick={() => handleSelect(doc)}
                    disabled={loadingDetailId === docId}
                  >
                    {loadingDetailId === docId ? "Cargando..." : "Seleccionar"}
                  </Button>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Paper sx={{ border: "1px solid #e0e0e0", overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 800 }}>Documento</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 800, textAlign: "right" }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments.map((doc) => {
                  const docId = doc?.id || doc?.documento_id;

                  return (
                    <TableRow key={docId} hover>
                      <TableCell>{getDocNumber(doc)}</TableCell>
                      <TableCell>{doc?.fecha_emision || ""}</TableCell>
                      <TableCell>{doc?.cliente || doc?.razon_social || ""}</TableCell>
                      <TableCell>{doc?.estado || ""}</TableCell>
                      <TableCell>{doc?.total ?? doc?.total_documento ?? ""}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ bgcolor: "#1565C0" }}
                          onClick={() => handleSelect(doc)}
                          disabled={loadingDetailId === docId}
                        >
                          {loadingDetailId === docId ? "Cargando..." : "Seleccionar"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        )}

        <Divider sx={{ mt: 2 }} />
      </DialogContent>
    </Dialog>
  );
}